import type { Context } from '@model/context';
import type { AudioService } from '@service/audio';
import type { AutoplayService } from '@service/autoplay';
import type { BrailleService } from '@service/braille';
import type { DisplayService } from '@service/display';
import type { HighContrastService } from '@service/highContrast';
import type { HighlightService } from '@service/highlight';
import type { NotificationService } from '@service/notification';
import type { RotorNavigationService } from '@service/rotor';
import type { TextService } from '@service/text';
import type { BrailleViewModel } from '@state/viewModel/brailleViewModel';
import type { ChatViewModel } from '@state/viewModel/chatViewModel';
import type { CommandPaletteViewModel } from '@state/viewModel/commandPaletteViewModel';
import type { DescriptionViewModel } from '@state/viewModel/descriptionViewModel';
import type { GoToExtremaViewModel } from '@state/viewModel/goToExtremaViewModel';
import type { HelpViewModel } from '@state/viewModel/helpViewModel';
import type { ReviewViewModel } from '@state/viewModel/reviewViewModel';
import type { RotorNavigationViewModel } from '@state/viewModel/rotorNavigationViewModel';
import type { SettingsViewModel } from '@state/viewModel/settingsViewModel';
import type { TextViewModel } from '@state/viewModel/textViewModel';
import type { Keys } from '@type/event';
import type { Command, CommandContext } from './command';
import { Scope } from '@type/event';
import {
  AutoplayBackwardCommand,
  AutoplayDownwardCommand,
  AutoplayForwardCommand,
  AutoplayUpwardCommand,
  ResetAutoplaySpeedCommand,
  SpeedDownAutoplayCommand,
  SpeedUpAutoplayCommand,
  StopAutoplayCommand,
} from './autoplay';
import {
  AnnounceCaptionCommand,
  AnnouncePointCommand,
  AnnouncePositionCommand,
  AnnounceSubtitleCommand,
  AnnounceTitleCommand,
  AnnounceXCommand,
  AnnounceYCommand,
  AnnounceZCommand,
} from './describe';
import { GoToExtremaToggleCommand } from './goTo';
import {
  GoToExtremaCloseCommand,
  GoToExtremaMoveDownCommand,
  GoToExtremaMoveUpCommand,
  GoToExtremaSelectCommand,
} from './goToExtremaNavigation';
import {
  EnterGridCellCommand,
  ExitGridCellCommand,
  GridCellMoveLeftCommand,
  GridCellMoveRightCommand,
} from './gridCell';
import {
  ExitBrailleAndSubplotCommand,
  MoveDownCommand,
  MoveLeftCommand,
  MoveRightCommand,
  MoveToBottomExtremeCommand,
  MoveToLeftExtremeCommand,
  MoveToNextTraceCommand,
  MoveToPrevTraceCommand,
  MoveToRightExtremeCommand,
  MoveToSubplotContextCommand,
  MoveToTopExtremeCommand,
  MoveToTraceContextCommand,
  MoveUpCommand,
} from './move';
import {
  RotorNavigationMoveDownCommand,
  RotorNavigationMoveLeftCommand,
  RotorNavigationMoveRightCommand,
  RotorNavigationMoveUpCommand,
  RotorNavigationNextNavUnitCommand,
  RotorNavigationPrevNavUnitCommand,
} from './rotorNavigation';
import {
  CommandPaletteCloseCommand,
  CommandPaletteMoveDownCommand,
  CommandPaletteMoveUpCommand,
  CommandPaletteSelectCommand,
  ToggleAudioCommand,
  ToggleBrailleCommand,
  ToggleChatCommand,
  ToggleCommandPaletteCommand,
  ToggleDescriptionCommand,
  ToggleHelpCommand,
  ToggleHighContrast,
  ToggleReviewCommand,
  ToggleScopeCommand,
  ToggleSettingsCommand,
  ToggleTextCommand,
} from './toggle';

/**
 * Factory for creating command instances based on key input.
 */
export class CommandFactory {
  private readonly context: Context;

  private readonly audioService: AudioService;
  private readonly autoplayService: AutoplayService;
  private readonly brailleService: BrailleService;
  private readonly displayService: DisplayService;
  private readonly highContrastService: HighContrastService;
  private readonly highlightService: HighlightService;
  private readonly notificationService: NotificationService;
  private readonly rotorService: RotorNavigationService;
  private readonly textService: TextService;

  private readonly brailleViewModel: BrailleViewModel;
  private readonly chatViewModel: ChatViewModel;
  private readonly commandPaletteViewModel: CommandPaletteViewModel;
  private readonly descriptionViewModel: DescriptionViewModel;
  private readonly goToExtremaViewModel: GoToExtremaViewModel;
  private readonly helpViewModel: HelpViewModel;
  private readonly reviewViewModel: ReviewViewModel;
  private readonly settingsViewModel: SettingsViewModel;
  private readonly textViewModel: TextViewModel;
  private readonly rotorNavigationViewModel: RotorNavigationViewModel;

  /**
   * Creates an instance of CommandFactory.
   * @param {CommandContext} commandContext - The command context containing services and view models.
   */
  public constructor(commandContext: CommandContext) {
    this.context = commandContext.context;

    this.audioService = commandContext.audioService;
    this.autoplayService = commandContext.autoplayService;
    this.brailleService = commandContext.brailleService;
    this.displayService = commandContext.displayService;
    this.highContrastService = commandContext.highContrastService;
    this.highlightService = commandContext.highlightService;
    this.notificationService = commandContext.notificationService;
    this.rotorService = commandContext.rotorNavigationService;
    this.textService = commandContext.textService;

    this.brailleViewModel = commandContext.brailleViewModel;
    this.chatViewModel = commandContext.chatViewModel;
    this.commandPaletteViewModel = commandContext.commandPaletteViewModel;
    this.descriptionViewModel = commandContext.descriptionViewModel;
    this.goToExtremaViewModel = commandContext.goToExtremaViewModel;
    this.helpViewModel = commandContext.helpViewModel;
    this.reviewViewModel = commandContext.reviewViewModel;
    this.settingsViewModel = commandContext.settingsViewModel;
    this.textViewModel = commandContext.textViewModel;
    this.rotorNavigationViewModel = commandContext.rotorNavigationViewModel;
  }

  /**
   * Creates a command instance based on the provided key input.
   * @param {Keys} command - The key command to create a Command instance for.
   * @returns {Command} The corresponding command instance for the given key.
   */
  public create(command: Keys): Command {
    switch (command) {
      case 'MOVE_UP':
        if (this.context.isRotorEnabled()) {
          return new RotorNavigationMoveUpCommand(
            this.rotorNavigationViewModel,
          );
        }
        return new MoveUpCommand(this.context);
      case 'MOVE_DOWN':
        if (this.context.isRotorEnabled()) {
          return new RotorNavigationMoveDownCommand(
            this.rotorNavigationViewModel,
          );
        }
        return new MoveDownCommand(this.context);
      case 'MOVE_LEFT':
        if (this.context.isRotorEnabled()) {
          return new RotorNavigationMoveLeftCommand(
            this.rotorNavigationViewModel,
          );
        }
        return new MoveLeftCommand(this.context);
      case 'MOVE_RIGHT':
        if (this.context.isRotorEnabled()) {
          return new RotorNavigationMoveRightCommand(
            this.rotorNavigationViewModel,
          );
        }
        return new MoveRightCommand(this.context);
      case 'MOVE_TO_TOP_EXTREME':
        return new MoveToTopExtremeCommand(this.context);
      case 'MOVE_TO_BOTTOM_EXTREME':
        return new MoveToBottomExtremeCommand(this.context);
      case 'MOVE_TO_LEFT_EXTREME':
        return new MoveToLeftExtremeCommand(this.context);
      case 'MOVE_TO_RIGHT_EXTREME':
        return new MoveToRightExtremeCommand(this.context);

      case 'MOVE_TO_TRACE_CONTEXT':
        return new MoveToTraceContextCommand(this.context, this.brailleService, this.displayService);
      case 'MOVE_TO_SUBPLOT_CONTEXT':
        return new MoveToSubplotContextCommand(this.context);
      case 'EXIT_BRAILLE_AND_SUBPLOT':
        return new ExitBrailleAndSubplotCommand(this.context, this.displayService);
      case 'MOVE_TO_NEXT_TRACE':
        return new MoveToNextTraceCommand(this.context);
      case 'MOVE_TO_PREV_TRACE':
        return new MoveToPrevTraceCommand(this.context);

      case 'TOGGLE_AUDIO':
        return new ToggleAudioCommand(this.audioService);
      case 'TOGGLE_BRAILLE':
        return new ToggleBrailleCommand(this.context, this.brailleViewModel);
      case 'TOGGLE_TEXT':
        return new ToggleTextCommand(this.textViewModel);
      case 'TOGGLE_REVIEW':
        return new ToggleReviewCommand(this.context, this.reviewViewModel);
      case 'TOGGLE_HIGH_CONTRAST':
        return new ToggleHighContrast(this.highContrastService);

      case 'TOGGLE_HELP':
        return new ToggleHelpCommand(this.helpViewModel);
      case 'TOGGLE_CHAT':
        return new ToggleChatCommand(this.chatViewModel);
      case 'TOGGLE_COMMAND_PALETTE':
        return new ToggleCommandPaletteCommand(this.commandPaletteViewModel);
      case 'TOGGLE_DESCRIPTION':
        return new ToggleDescriptionCommand(this.descriptionViewModel);
      case 'TOGGLE_SETTINGS':
        return new ToggleSettingsCommand(this.settingsViewModel);

      case 'GO_TO_EXTREMA_MOVE_UP':
        return new GoToExtremaMoveUpCommand(this.goToExtremaViewModel);
      case 'GO_TO_EXTREMA_MOVE_DOWN':
        return new GoToExtremaMoveDownCommand(this.goToExtremaViewModel);
      case 'GO_TO_EXTREMA_SELECT':
        return new GoToExtremaSelectCommand(this.goToExtremaViewModel);
      case 'GO_TO_EXTREMA_CLOSE':
        return new GoToExtremaCloseCommand(this.goToExtremaViewModel);
      case 'GO_TO_EXTREMA_TOGGLE':
        return new GoToExtremaToggleCommand(
          this.context,
          this.goToExtremaViewModel,
        );
      case 'COMMAND_PALETTE_MOVE_UP':
        return new CommandPaletteMoveUpCommand(this.commandPaletteViewModel);
      case 'COMMAND_PALETTE_MOVE_DOWN':
        return new CommandPaletteMoveDownCommand(this.commandPaletteViewModel);
      case 'COMMAND_PALETTE_SELECT':
        return new CommandPaletteSelectCommand(this.commandPaletteViewModel);
      case 'COMMAND_PALETTE_CLOSE':
        return new CommandPaletteCloseCommand(this.commandPaletteViewModel);
      case 'ANNOUNCE_X':
        return new AnnounceXCommand(this.context, this.textViewModel, this.audioService, this.textService, this.displayService);
      case 'ANNOUNCE_Y':
        return new AnnounceYCommand(this.context, this.textViewModel, this.audioService, this.textService, this.displayService);
      case 'ANNOUNCE_Z':
        return new AnnounceZCommand(this.context, this.textViewModel, this.audioService, this.textService, this.displayService);
      case 'ANNOUNCE_POINT':
        return new AnnouncePointCommand(
          this.context,
          this.audioService,
          this.highlightService,
          this.brailleViewModel,
          this.textViewModel,
          this.textService,
          this.displayService,
        );
      case 'ANNOUNCE_TITLE':
        return new AnnounceTitleCommand(this.context, this.textViewModel, this.audioService, this.textService, this.displayService);
      case 'ANNOUNCE_SUBTITLE':
        return new AnnounceSubtitleCommand(this.context, this.textViewModel, this.audioService, this.textService, this.displayService);
      case 'ANNOUNCE_CAPTION':
        return new AnnounceCaptionCommand(this.context, this.textViewModel, this.audioService, this.textService, this.displayService);
      case 'ANNOUNCE_POSITION':
        return new AnnouncePositionCommand(
          this.context,
          this.textService,
          this.textViewModel,
          this.audioService,
          this.displayService,
        );

      case 'ACTIVATE_FIGURE_LABEL_SCOPE':
        return new ToggleScopeCommand(this.context, Scope.FIGURE_LABEL, this.textViewModel, this.displayService);
      case 'DEACTIVATE_FIGURE_LABEL_SCOPE':
        return new ToggleScopeCommand(this.context, Scope.FIGURE_LABEL);
      case 'ACTIVATE_TRACE_LABEL_SCOPE':
        return new ToggleScopeCommand(this.context, Scope.TRACE_LABEL, this.textViewModel, this.displayService);
      case 'DEACTIVATE_TRACE_LABEL_SCOPE':
        return new ToggleScopeCommand(this.context, Scope.TRACE);
      case 'AUTOPLAY_UPWARD':
        return new AutoplayUpwardCommand(this.context, this.autoplayService);
      case 'AUTOPLAY_DOWNWARD':
        return new AutoplayDownwardCommand(this.context, this.autoplayService);
      case 'AUTOPLAY_FORWARD':
        return new AutoplayForwardCommand(this.context, this.autoplayService);
      case 'AUTOPLAY_BACKWARD':
        return new AutoplayBackwardCommand(this.context, this.autoplayService);
      case 'STOP_AUTOPLAY':
        return new StopAutoplayCommand(this.autoplayService);
      case 'SPEED_UP_AUTOPLAY':
        return new SpeedUpAutoplayCommand(this.autoplayService);
      case 'SPEED_DOWN_AUTOPLAY':
        return new SpeedDownAutoplayCommand(this.autoplayService);
      case 'RESET_AUTOPLAY_SPEED':
        return new ResetAutoplaySpeedCommand(this.autoplayService);
      case 'ROTOR_NEXT_NAV':
        return new RotorNavigationNextNavUnitCommand(
          this.context,
          this.rotorNavigationViewModel,
        );
      case 'ROTOR_PREV_NAV':
        return new RotorNavigationPrevNavUnitCommand(
          this.context,
          this.rotorNavigationViewModel,
        );

      // Grid cell navigation
      case 'ENTER_GRID_CELL':
        return new EnterGridCellCommand(this.context, this.notificationService);
      case 'EXIT_GRID_CELL':
        return new ExitGridCellCommand(this.context);
      case 'GRID_CELL_MOVE_LEFT':
        return new GridCellMoveLeftCommand(this.context);
      case 'GRID_CELL_MOVE_RIGHT':
        return new GridCellMoveRightCommand(this.context);

      default:
        throw new Error(`Invalid command name: ${command}`);
    }
  }
}
